/**
 * Mapping the portal's accommodation accessibility requirements onto what Nuitee (LiteAPI)
 * actually knows about a hotel.
 *
 * Measured live against the production key, Sydney, 200 hotels:
 *
 *   id  48  Lift / Elevator                 174/200
 *   id  25  Facilities for disabled guests  143/200
 *   id 184  Accessible parking              108/200
 *   id 185  Wheelchair accessible            91/200
 *   id 186  Toilet with grab rails           75/200
 *   id 589  Roll-in shower (select rooms)      0/200   ← no data at all
 *   id 588  Accessible bathroom (select)       0/200   ← no data at all
 *   id 946  Visual alarms in hallways          0/200   ← no data at all
 *   id 593  Braille or raised signage          2/200   ← too sparse to filter on
 *
 * ⚠️ `hotel.accessibilityAttributes` LOOKS like the answer and is not. Every hotel carries the
 * field with a precise schema — `rampAngle`, `entranceDoorWidth`, `showerChair`,
 * `distanceFromTheElevatorToTheAccessibleRoom` — and on all 200 hotels every value was null, 0 or
 * empty. Do not build anything on it without re-measuring first.
 *
 * ⚠️ The `facilityIds=` QUERY PARAM is not trustworthy for filtering. Measured: `facilityIds=48`
 * returned all 200 hotels when only 174 carry id 48, and `facilityIds=186` returned 108 when 75
 * carry it — matching neither AND nor OR of the data the same endpoint returns. Other spellings
 * (`facilities=`, `amenities=`, `hotelFacilities=`) are accepted with HTTP 200 and silently
 * ignored, which is the failure mode that looks like success. Filter in the client instead, on
 * the `facilityIds` array `/data/hotels` already returns on every hotel — it costs no extra call.
 *
 * ⚠️ This can NARROW a list. It can never CONFIRM a requirement is met. Supplier-reported
 * facilities are coarse ("Facilities for disabled guests" says nothing about door widths) and
 * patchy. Anything booked against a real access need still has to be confirmed with the property,
 * and the UI must say so rather than implying the filter did the checking.
 */

/**
 * Portal requirement key → the facility ids that are decent evidence for it.
 *
 * `verifiable: false` means the API has no usable signal — the requirement is still shown to
 * whoever is booking, flagged as needing confirmation, but it is never offered as a filter that
 * would silently empty the results.
 */
export const ACCESSIBILITY_FACILITY_MAP = {
  grab_rails:         { verifiable: true,  facilityIds: [186],      label: 'Toilet with grab rails' },
  accessible_parking: { verifiable: true,  facilityIds: [184],      label: 'Accessible parking' },
  no_steps:           { verifiable: true,  facilityIds: [185],      label: 'Step-free / wheelchair accessible' },
  ground_floor:       { verifiable: true,  facilityIds: [48],       label: 'Lift to upper floors' },

  // No usable data on this account — see the measurements above.
  roll_in_shower:     { verifiable: false, facilityIds: [589, 588] },
  wide_doorways:      { verifiable: false, facilityIds: [] },
  hearing_loop:       { verifiable: false, facilityIds: [946, 593] },
  bath_hoist:         { verifiable: false, facilityIds: [] },
  carer_bed:          { verifiable: false, facilityIds: [] },
  adjustable_bed:     { verifiable: false, facilityIds: [] },
};

/**
 * A general "this property reports some disabled-guest provision" signal. Offered as its own chip
 * because it is the best-populated field by a distance (143/200), and a useful first cut when the
 * specific requirement isn't one the API covers.
 */
export const GENERAL_ACCESS_FILTER = {
  key: 'any_accessible',
  label: 'Reports accessibility provision',
  facilityIds: [25],
};

/** The chips worth showing — only requirements the API can actually speak to. */
export const ACCESSIBILITY_FILTERS = [
  GENERAL_ACCESS_FILTER,
  ...Object.entries(ACCESSIBILITY_FACILITY_MAP)
    .filter(([, v]) => v.verifiable)
    .map(([key, v]) => ({ key, label: v.label, facilityIds: v.facilityIds })),
];

export const isVerifiableRequirement = (key) => !!ACCESSIBILITY_FACILITY_MAP[key]?.verifiable;

/** Does this hotel report every one of the selected filters? */
export function hotelMeetsAccessFilters(hotel, selectedKeys = []) {
  if (!selectedKeys.length) return true;
  const ids = new Set(hotel?.facilityIds || []);
  return selectedKeys.every((key) => {
    const filter = ACCESSIBILITY_FILTERS.find(f => f.key === key);
    // An unknown key must not silently pass — but it must not silently exclude everything either.
    if (!filter) return true;
    return filter.facilityIds.some(id => ids.has(id));
  });
}

/** How many of these hotels report each filter, so a chip can say what choosing it would cost. */
export function accessFilterCounts(hotels = []) {
  const counts = {};
  for (const f of ACCESSIBILITY_FILTERS) {
    counts[f.key] = hotels.filter(h => f.facilityIds.some(id => (h?.facilityIds || []).includes(id))).length;
  }
  return counts;
}
